/**
 * NewBatchDialog — submit a batch against one pipeline (TRA-863).
 *
 * Pick one of the team's assigned S3 connections, point at an input path
 * and an output path, set the pipeline's options. The API proves read on
 * the input and write on the output synchronously, so a failure here shows
 * the actual AWS error, and success means the batch is already queued.
 * Same shape as the benchmark ingestion dialog, with an output path and the
 * pipeline's own options added.
 */

import { useState } from 'react'
import { Loader2 } from 'lucide-react'

import type {
  BatchCreate,
  BatchResponse,
  PipelineInfo,
  TeamConnectionItem,
} from '@/api/generated/model'
import { getApiErrorDetail } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateBatch } from '@/hooks/usePipelines'
import { isS3Path } from '@/lib/s3'

import {
  initialOptionValues,
  optionFieldsFromSchema,
  optionsForSubmit,
  type OptionField,
  type OptionValues,
} from './batchOptions'
import { idempotencyKeyFor } from './idempotency'

interface NewBatchDialogProps {
  pipeline: PipelineInfo
  connections: TeamConnectionItem[]
  onClose: () => void
  onCreated: (batch: BatchResponse) => void
}

function OptionInput({
  field,
  value,
  onChange,
}: {
  field: OptionField
  value: string | boolean
  onChange: (next: string | boolean) => void
}): React.JSX.Element {
  const id = `batch-option-${field.name}`
  if (field.kind === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={value === true}
          onCheckedChange={(checked) => {
            onChange(checked === true)
          }}
        />
        <Label htmlFor={id}>{field.label}</Label>
      </div>
    )
  }
  const text = typeof value === 'string' ? value : ''
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{field.label}</Label>
      {field.choices === null ? (
        <Input
          id={id}
          type={field.kind === 'number' ? 'number' : 'text'}
          value={text}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          className="font-mono"
          autoComplete="off"
          spellCheck={false}
        />
      ) : (
        <Select value={text} onValueChange={onChange}>
          <SelectTrigger id={id} aria-label={field.label}>
            <SelectValue placeholder="Choose a value…" />
          </SelectTrigger>
          <SelectContent>
            {field.choices.map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {field.description !== '' && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
    </div>
  )
}

export function NewBatchDialog({
  pipeline,
  connections,
  onClose,
  onCreated,
}: NewBatchDialogProps): React.JSX.Element {
  const [fields] = useState(() => optionFieldsFromSchema(pipeline.options_schema))
  const [connectionId, setConnectionId] = useState<string>(
    connections.length === 1 ? (connections[0]?.id ?? '') : '',
  )
  const [inputPath, setInputPath] = useState('')
  const [outputPath, setOutputPath] = useState('')
  const [optionValues, setOptionValues] = useState<OptionValues>(() =>
    initialOptionValues(fields),
  )
  const [error, setError] = useState<string | null>(null)
  // Half of the Idempotency-Key (see idempotency.ts): the other half is a
  // digest of what is submitted, so a retry after a network blip returns
  // the batch the first attempt created, while an edited form does not.
  const [nonce] = useState(() => crypto.randomUUID())
  const mutation = useCreateBatch()
  const selectedConnection = connections.find((c) => c.id === connectionId)

  const canSubmit =
    connectionId !== '' && isS3Path(inputPath) && isS3Path(outputPath) && !mutation.isPending

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (!canSubmit) return
    setError(null)
    try {
      const payload: BatchCreate = {
        pipeline: pipeline.name,
        connection_id: connectionId,
        input_path: inputPath.trim(),
        output_path: outputPath.trim(),
        options: optionsForSubmit(fields, optionValues),
      }
      const response = await mutation.mutateAsync({
        data: payload,
        idempotencyKey: await idempotencyKeyFor(nonce, payload),
      })
      if (response.status !== 201) {
        // orvalFetch throws on non-2xx, so this branch is unreachable; it
        // exists to narrow the response union without hiding a regression.
        throw new Error(`Submit returned unexpected status ${String(response.status)}`)
      }
      onCreated(response.data)
    } catch (err) {
      setError(
        getApiErrorDetail(err) ?? 'The batch was not submitted. Check the paths and try again',
      )
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New batch</DialogTitle>
          <DialogDescription>
            Runs {pipeline.display_name} over every file under the input path.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="batch-connection">S3 account</Label>
            <Select value={connectionId} onValueChange={setConnectionId}>
              <SelectTrigger id="batch-connection" aria-label="S3 account">
                <SelectValue placeholder="Choose an S3 account…" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((connection) => (
                  <SelectItem key={connection.id} value={connection.id}>
                    {connection.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedConnection !== undefined && selectedConnection.principal_arn !== '' && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Your bucket must grant this principal read on the input path and write on
                  the output path:
                </p>
                <div className="flex items-center gap-1 border border-border bg-muted px-2 py-1">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs">
                    {selectedConnection.principal_arn}
                  </code>
                  <CopyButton
                    value={selectedConnection.principal_arn}
                    label="Copy principal ARN"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="batch-input-path">Input path</Label>
            <Input
              id="batch-input-path"
              value={inputPath}
              onChange={(event) => {
                setInputPath(event.target.value)
              }}
              placeholder="s3://bucket/documents/"
              className="font-mono"
              aria-invalid={inputPath.length > 0 && !isS3Path(inputPath)}
              aria-describedby="batch-input-path-help"
              autoComplete="off"
              spellCheck={false}
            />
            <p id="batch-input-path-help" className="text-xs text-muted-foreground">
              Every object under this path runs through the pipeline.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="batch-output-path">Output path</Label>
            <Input
              id="batch-output-path"
              value={outputPath}
              onChange={(event) => {
                setOutputPath(event.target.value)
              }}
              placeholder="s3://bucket/redacted/"
              className="font-mono"
              aria-invalid={outputPath.length > 0 && !isS3Path(outputPath)}
              aria-describedby="batch-output-path-help"
              autoComplete="off"
              spellCheck={false}
            />
            <p id="batch-output-path-help" className="text-xs text-muted-foreground">
              Outputs land under this path and mirror the input tree. It cannot overlap the
              input path.
            </p>
          </div>
          {fields.length > 0 && (
            <fieldset className="space-y-3 border-t border-border pt-4">
              <legend className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Options
              </legend>
              {fields.map((field) => (
                <OptionInput
                  key={field.name}
                  field={field}
                  value={optionValues[field.name] ?? ''}
                  onChange={(next) => {
                    setOptionValues((prev) => ({ ...prev, [field.name]: next }))
                  }}
                />
              ))}
            </fieldset>
          )}
          {error !== null && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mutation.isPending ? 'Submitting…' : 'Submit batch'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
